/**
 * Payload cross-domain auth — v.1-D §7.4.
 *
 * Verbatim port from `frontend/src/lib/payloadAuth.ts` with ONE change
 * (Decision D-017): COOKIE_DOMAIN is read from `env.COOKIE_DOMAIN` instead of
 * being hardcoded to `.mediatiquepress.one`. This permits local-dev sign-in
 * on `localhost` without breaking production cross-domain cookies.
 *
 * Exports preserved: PayloadAuthProvider, useUser, useAuth, useCsrfToken,
 * parseCrossDomainCookie, COOKIE_NAME, CSRF_HEADER_NAME, COOKIE_DOMAIN.
 */
import {
  createElement,
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { env } from './env';
import { logger } from './logger';

export const COOKIE_DOMAIN = env.COOKIE_DOMAIN;
export const COOKIE_NAME = 'payload-token';
export const CSRF_HEADER_NAME = 'X-Csrf-Token';

export interface PayloadUser {
  id: string;
  email: string;
  roles: string[];
}

export interface PayloadAuthContextValue {
  user: PayloadUser | null;
  loading: boolean;
  csrfToken: string | null;
  /** Raw JWT token string (for Convex Authorization header). */
  token: string | null;
  /**
   * Credential sign-in via Payload's `/api/users/login`. Throws on failure.
   * On HTTP 423 (account locked) the error message is "Account locked" so
   * the caller can route to `/sign-in?reason=locked` per §14.9.
   * NOTE: this is the v.1-D-correct path — the previous draft fabricated
   * a `lockedFlag` on the context (§0.4 item 4). The 423 path is detected
   * inside this method, not via a stale flag.
   */
  signIn: (email: string, password: string) => Promise<void>;
  /** Clears the local cookie + state; the caller may then redirect. */
  signOut: () => Promise<void>;
}

const PayloadAuthContext = createContext<PayloadAuthContextValue>({
  user: null,
  loading: true,
  csrfToken: null,
  token: null,
  signIn: async () => {
    throw new Error('PayloadAuthProvider is not mounted');
  },
  signOut: async () => {
    throw new Error('PayloadAuthProvider is not mounted');
  },
});

interface JwtPayload {
  sub: string;
  email?: string;
  roles?: string[];
  exp?: number;
  iat?: number;
  csrf?: string;
  [key: string]: unknown;
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
}

function isExpired(payload: JwtPayload): boolean {
  if (payload.exp == null) return false;
  return payload.exp < Math.floor(Date.now() / 1000);
}

export function parseCrossDomainCookie(): JwtPayload | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...rest] = cookie.trim().split('=');
    if (name === COOKIE_NAME) {
      const token = rest.join('=');
      if (!token) return null;
      const payload = decodeJwtPayload(token);
      if (!payload) return null;
      if (isExpired(payload)) return null;
      return payload;
    }
  }
  return null;
}

function extractTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...rest] = cookie.trim().split('=');
    if (name === COOKIE_NAME) {
      const token = rest.join('=');
      return token || null;
    }
  }
  return null;
}

function generateCsrfToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function refreshFromCookie(): {
  user: PayloadUser | null;
  token: string | null;
  csrfToken: string | null;
} {
  const claims = parseCrossDomainCookie();
  const rawToken = extractTokenFromCookie();
  if (claims && rawToken) {
    return {
      user: {
        id: claims.sub,
        email: claims.email ?? '',
        roles: Array.isArray(claims.roles) ? claims.roles : [],
      },
      token: rawToken,
      csrfToken:
        typeof claims.csrf === 'string' ? claims.csrf : generateCsrfToken(),
    };
  }
  return { user: null, token: null, csrfToken: null };
}

export function PayloadAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PayloadUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const snapshot = refreshFromCookie();
    setUser(snapshot.user);
    setToken(snapshot.token);
    setCsrfToken(snapshot.csrfToken);
    setLoading(false);
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    const response = await fetch(`${env.PAYLOAD_URL}/api/users/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (response.status === 423) {
      // §14.9 — Payload returns 423 when the account is locked. Surface the
      // condition via a thrown error; the caller routes to /sign-in?reason=locked.
      logger.warn('payload_signin_locked', { email });
      throw new Error('Account locked');
    }
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      logger.warn('payload_signin_failed', { status: response.status, detail });
      throw new Error('Invalid email or password');
    }
    const snapshot = refreshFromCookie();
    setUser(snapshot.user);
    setToken(snapshot.token);
    setCsrfToken(snapshot.csrfToken);
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await fetch(`${env.PAYLOAD_URL}/api/users/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      // The cookie is HttpOnly and cross-domain; a network failure here still
      // leaves the local state in an authenticated position. Log so the
      // operator can investigate but otherwise proceed to clear local state.
      logger.warn('payload_signout_network_error', {
        message: error instanceof Error ? error.message : String(error),
      });
    }
    setUser(null);
    setToken(null);
    setCsrfToken(null);
  }, []);

  const value: PayloadAuthContextValue = {
    user,
    loading,
    csrfToken,
    token,
    signIn,
    signOut,
  };
  return createElement(PayloadAuthContext.Provider, { value }, children);
}

export function useUser(): PayloadUser | null {
  return useContext(PayloadAuthContext).user;
}

export function useAuth(): PayloadAuthContextValue {
  return useContext(PayloadAuthContext);
}

export function useCsrfToken(): string | null {
  return useContext(PayloadAuthContext).csrfToken;
}
